"use client";
import React, { useState, useCallback, useEffect } from 'react';
import { useSelectedCurrency } from '../../hooks';
import { currencies } from '@/constants';
import { ExpandMoreIcon, CloseIcon } from '../icons';
import styles from './CurrencySelector.module.css';

interface CurrencySelectorProps {
  onCurrencyChange?: (currency: string) => void;
  disabled?: boolean;
  variant?: 'full';
}

export const CurrencySelector: React.FC<CurrencySelectorProps> = ({ onCurrencyChange, disabled, variant }) => {
  const [isOpen, setIsOpen] = useState(false);
  const { selectedCurrency, updateCurrency } = useSelectedCurrency();

  const openModal = useCallback(() => {
    if (disabled) return;
    setIsOpen(true);
  }, [disabled]);

  const closeModal = useCallback(() => {
    setIsOpen(false);
  }, []);

  // Lock body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
  }, [isOpen]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeModal();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, closeModal]);

  const handleCurrencySelect = useCallback((currencyCode: string) => {
    updateCurrency(currencyCode);
    setIsOpen(false);
    onCurrencyChange?.(currencyCode);
  }, [updateCurrency, onCurrencyChange]);

  const selectedCurrencyData = currencies.find(c => c.code === selectedCurrency) || currencies[0];

  return (
    <>
      {/* Trigger Button */}
      <div
        className={`${styles.currencyWrapper}${disabled ? ` ${styles.disabled}` : ''}${variant === 'full' ? ` ${styles.full}` : ''}`}
        onClick={openModal}
        title={disabled ? 'Currency selector only works on homepage and gift cards pages' : undefined}
      >
        <div className={styles.currencyButton}>
          <span className={styles.labelShort}>{selectedCurrencyData.symbol}</span>
          <span className={styles.labelFull}>{selectedCurrencyData.code}</span>
          <ExpandMoreIcon className={styles.expandIcon} />
        </div>
      </div>

      {/* Modal Popup */}
      {isOpen && (
        <div className={styles.overlay} onClick={closeModal}>
          <div className={styles.modal} onClick={(e: React.MouseEvent) => e.stopPropagation()}>
            {/* Header */}
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>Currency</h3>
              <button className={styles.closeButton} onClick={closeModal} aria-label="Close">
                <CloseIcon fontSize="small" />
              </button>
            </div>

            {/* Currency List */}
            <div className={styles.currencyList}>
              {currencies.map((currency) => {
                const isSelected = currency.code === selectedCurrency;
                return (
                  <div
                    key={currency.code}
                    className={`${styles.currencyOption}${isSelected ? ` ${styles.selected}` : ''}`}
                    onClick={() => handleCurrencySelect(currency.code)}
                  >
                    <div className={styles.currencySymbolBadge}>
                      <span className={`${styles.symbolText}${isSelected ? ` ${styles.selectedSymbol}` : ''}`}>
                        {currency.symbol}
                      </span>
                    </div>
                    <div className={styles.currencyInfo}>
                      <span className={`${styles.currencyCode}${isSelected ? ` ${styles.selectedCode}` : ''}`}>
                        {currency.code}
                      </span>
                      <span className={styles.currencyLabel}>{currency.label}</span>
                    </div>
                    {isSelected && <div className={styles.checkmark}>✓</div>}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </>
  );
};
